// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { SEED_ORG_PLACEHOLDER, SEED_WORKSPACE_PLACEHOLDER } from './constants'
import type { SeedSchema } from './constants'

/**
 * Seed schema for the entityassignments collection.
 * Generalized junction table linking team members to any entity with roles.
 */
export const entityAssignmentsSchema: SeedSchema = {
  name: 'entityassignments',
  title: 'Entity Assignments',
  description: 'Junction records linking team members to any entity with role classification',
  category: 'core',
  target: 'records',
  titleField: 'entityId',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'easn' },
  defaultSort: { field: 'assignedAt', direction: 'desc' },
  fields: [
    {
      name: 'entityType',
      label: 'Entity Type',
      type: 'select',
      required: true,
      order: 1,
      options: [
        { value: 'task', label: 'Task' },
        { value: 'project', label: 'Project' },
        { value: 'milestone', label: 'Milestone' },
        { value: 'goal', label: 'Goal' },
        { value: 'initiative', label: 'Initiative' },
        { value: 'product', label: 'Product' },
        { value: 'businessplan', label: 'Business Plan' },
        { value: 'roadmap', label: 'Roadmap' },
        { value: 'document', label: 'Document' },
        { value: 'proposal', label: 'Proposal' },
        { value: 'mission', label: 'Mission' },
        { value: 'deal', label: 'Deal' },
        { value: 'campaign', label: 'Campaign' },
        { value: 'codebase', label: 'Codebase' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'entityId',
      label: 'Entity',
      type: 'text',
      required: true,
      order: 2,
      config: { helpText: 'ID of the assigned entity' },
    },
    {
      name: 'teamMemberId',
      label: 'Team Member',
      type: 'relation',
      required: true,
      order: 3,
      relationSchema: 'teammembers',
      config: {},
    },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      required: true,
      order: 4,
      options: [
        { value: 'owner', label: 'Owner' },
        { value: 'contributor', label: 'Contributor' },
        { value: 'reviewer', label: 'Reviewer' },
        { value: 'observer', label: 'Observer' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'assignedAt',
      label: 'Assigned At',
      type: 'date',
      required: true,
      order: 5,
      config: { helpText: 'Timestamp when the assignment was created' },
    },
    {
      name: 'assignedBy',
      label: 'Assigned By',
      type: 'text',
      required: true,
      order: 6,
      config: { helpText: 'User ID of the person who created this assignment' },
    },
  ],
}

export const assignmentSeeds: SeedSchema[] = [entityAssignmentsSchema]
